<template>
  <div class="video-translation-view">
    <!-- Toast 提示组件 -->
    <div v-if="toast.show" class="toast-container" :class="toast.type">
      <div class="toast-content">
        <span class="toast-icon">{{ toast.icon }}</span>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </div>

    <!-- 视频重新配音主界面 -->
    <div class="video-translation">
      <h2>视频重新配音</h2>
      <div class="notice-text">
        注意：参考音频时长需大于 {{ minAudioSeconds }} 秒且小于 {{ maxAudioSeconds }} 秒，视频文案请使用 UTF-8 编码的 txt 文件；多行文案将使用同一参考音色逐段生成。
      </div>
      <div class="subtitle-desc">
        去除原声、克隆音色、重新配音
      </div>

      <!-- ==================== 重新配音模式 ==================== -->
      <div class="content-area">
        <div class="left-area">
          <div class="video-container">
            <div
              v-if="!selectedVideo"
              class="upload-box"
              @click="triggerVideoUpload"
              @drop.prevent="handleVideoDrop"
              @dragover.prevent="isDraggingVideo = true"
              @dragleave.prevent="isDraggingVideo = false"
              :class="{ 'dragging': isDraggingVideo }"
            >
              <div class="upload-icon">+</div>
              <p>点击上传或拖拽本人视频</p>
              <small>去除原声仅保留画面，单文件不超过 2GB</small>
              <input
                type="file"
                ref="videoInput"
                @change="handleVideoSelect"
                accept="video/*"
                style="display: none;"
              />
            </div>
            <div v-else-if="selectedVideo" class="video-player-wrapper">
              <button class="delete-video-btn" @click="resetVideo" aria-label="移除已上传的视频">×</button>
              <video ref="videoPlayer" controls playsinline :src="videoUrl" @loadedmetadata="onVideoLoaded"></video>
              <span class="video-file-chip">{{ selectedVideo.name }}</span>
            </div>
          </div>
        </div>

        <div class="right-container">
          <!-- 参考音频：放在“将视频翻译成”的位置 -->
          <div
            class="setting-card"
            :class="{ 'dragging': isDraggingAudio }"
            @click="triggerAudioUpload"
            @drop.prevent="handleAudioDrop"
            @dragover.prevent="isDraggingAudio = true"
            @dragleave.prevent="isDraggingAudio = false"
          >
            <div class="setting-content">
              <h4>参考音频</h4>
              <p v-if="!selectedAudio">点击上传 {{ minAudioSeconds }}~{{ maxAudioSeconds }} 秒的参考音频</p>
              <p v-else class="file-name" :title="selectedAudio.name">{{ selectedAudio.name }}</p>
              <p
                v-if="selectedAudio && audioDuration > 0 && !isAudioDurationValid"
                class="audio-duration-error"
              >
                时长需大于 {{ minAudioSeconds }} 秒且小于 {{ maxAudioSeconds }} 秒（当前 {{ formatDuration(audioDuration) }}）
              </p>
            </div>
            <div class="setting-control" @click.stop>
              <template v-if="selectedAudio">
                <button
                  type="button"
                  class="audio-play-btn"
                  :aria-label="isAudioPlaying ? '暂停播放' : '播放参考音频'"
                  :title="isAudioPlaying ? '暂停播放' : '播放参考音频'"
                  @click="toggleAudioPlayback"
                >
                  <svg v-if="!isAudioPlaying" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                  <svg v-else viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6z" /><path d="M14 5h4v14h-4z" /></svg>
                </button>
                <button
                  type="button"
                  class="audio-remove-btn"
                  aria-label="移除参考音频"
                  title="移除参考音频"
                  @click="resetAudio"
                >×</button>
                <audio
                  ref="audioPlayer"
                  class="audio-hidden"
                  preload="metadata"
                  :src="audioUrl"
                  @loadedmetadata="onAudioLoaded"
                  @ended="onAudioEnded"
                  @play="isAudioPlaying = true"
                  @pause="isAudioPlaying = false"
                ></audio>
              </template>
              <span v-else class="control-hint">未上传</span>
            </div>
          </div>

          <!-- 视频文案：放在“返回字幕类型”的位置 -->
          <div
            class="setting-card"
            :class="{ 'dragging': isDraggingScript }"
            @click="triggerScriptUpload"
            @drop.prevent="handleScriptDrop"
            @dragover.prevent="isDraggingScript = true"
            @dragleave.prevent="isDraggingScript = false"
          >
            <div class="setting-content">
              <h4>视频文案</h4>
              <p v-if="!scriptFile">点击上传 txt 文案</p>
              <p v-else class="file-name" :title="scriptFile.name">{{ scriptFile.name }}</p>
            </div>
            <div class="setting-control" @click.stop>
              <button
                v-if="scriptFile"
                type="button"
                class="audio-remove-btn"
                aria-label="移除视频文案"
                title="移除视频文案"
                @click="resetScript"
              >×</button>
              <span v-else class="control-hint">未上传</span>
            </div>
          </div>

          <div class="setting-card">
            <div class="setting-content">
              <h4>音唇同步</h4>
              <p>生成时同步调整口型以匹配新配音，费用更高</p>
            </div>
            <div class="setting-control">
              <label class="toggle-switch">
                <input type="checkbox" v-model="lipSync" />
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div class="setting-card">
            <div class="setting-content">
              <h4>写入字幕</h4>
              <p>将文案烧录为硬字幕，生成后不可关闭</p>
            </div>
            <div class="setting-control">
              <label class="toggle-switch">
                <input type="checkbox" v-model="writeSubtitle" />
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div class="action-buttons">
            <button
              class="translate-button"
              @click="startDub"
              :disabled="!canSubmit"
            >
              {{ isProcessing ? '正在提交…' : '开始重新配音' }}
            </button>
          </div>
        </div>
      </div>

      <p class="terms">
        仅对本人拥有或已获合法授权的素材进行配音，并遵守相关使用规范。
      </p>
    </div>

    <!-- 隐藏的文件输入（参考音频 / 视频文案） -->
    <input ref="audioInput" type="file" accept="audio/*" hidden @change="handleAudioSelect" />
    <input ref="scriptInput" type="file" accept=".txt,text/plain" hidden @change="handleScriptSelect" />

    <!-- 确认收费对话框 -->
    <div v-if="showConfirmPricing" class="confirm-dialog-overlay">
      <div class="confirm-dialog">
        <h3>确认费用</h3>
        <p>
          视频时长 {{ formatDuration(videoDuration) }}，
          按 {{ costPerMinute }} 元/分钟计费，预计扣除 ¥{{ confirmCost.toFixed(2) }}。
        </p>
        <div class="confirm-dialog-buttons">
          <button class="cancel-btn" @click="showConfirmPricing = false">取消</button>
          <button class="confirm-btn" @click="confirmAndStartDub">确认提交</button>
        </div>
      </div>
    </div>

    <!-- 处理中提示 -->
    <div v-if="isProcessing" class="processing-overlay">
      <div class="processing-dialog">
        <div class="spinner"></div>
        <h3>正在上传并提交</h3>

        <!-- 上传进度：三个文件（视频/音频/文案）并发上传，这里显示合计进度 -->
        <div v-if="isUploading" class="upload-progress">
          <div class="upload-progress-track">
            <div class="upload-progress-bar" :style="{ width: uploadPercent + '%' }"></div>
          </div>
          <p class="upload-progress-text">
            {{ uploadPercent.toFixed(1) }}% · {{ uploadLoadedText }} · {{ uploadSpeedText }}
          </p>
          <p v-if="uploadSlowTip" class="upload-slow-tip">{{ uploadSlowTip }}</p>
          <button class="cancel-upload-btn" @click="cancelUpload">取消上传</button>
        </div>
        <p v-else>文件上传到七牛云后，后台将自动排队处理。</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUpQiniuToken,
  uploadToQiniu,
  saveVideoDubFileUrl
} from '@/api/upfile'
import { QINIU_BASE_URL } from '@/api/constants'
import { mapGetters } from 'vuex'
import uploadProgressMixin from '@/utils/uploadProgressMixin'

const MIN_AUDIO_SECONDS = 6
const MAX_AUDIO_SECONDS = 15
const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024
const MAX_AUDIO_BYTES = 50 * 1024 * 1024

export default {
  name: 'VideoDubView',
  mixins: [uploadProgressMixin],
  data () {
    return {
      selectedVideo: null,
      videoUrl: '',
      videoDuration: 0,
      isDraggingVideo: false,

      selectedAudio: null,
      audioUrl: '',
      audioDuration: 0,
      isAudioPlaying: false,
      isDraggingAudio: false,

      scriptFile: null,
      scriptPreview: '',
      isDraggingScript: false,

      lipSync: false,
      writeSubtitle: false,
      isProcessing: false,
      showConfirmPricing: false,
      confirmCost: 0,
      toast: {
        show: false,
        type: 'success',
        message: '',
        icon: '✓'
      }
    }
  },
  computed: {
    ...mapGetters(['balance', 'isLoggedIn']),
    costPerMinute () {
      return this.lipSync ? 4 : 2
    },
    estimatedCost () {
      return (this.videoDuration / 60) * this.costPerMinute
    },
    canSubmit () {
      return this.selectedVideo &&
        this.selectedAudio &&
        this.scriptFile &&
        this.videoDuration > 0 &&
        this.isAudioDurationValid &&
        !this.isProcessing
    },
    isAudioDurationValid () {
      return this.audioDuration > MIN_AUDIO_SECONDS &&
        this.audioDuration < MAX_AUDIO_SECONDS
    },
    minAudioSeconds () {
      return MIN_AUDIO_SECONDS
    },
    maxAudioSeconds () {
      return MAX_AUDIO_SECONDS
    }
  },
  beforeDestroy () {
    this.resetVideoObjectUrl()
    this.resetAudioObjectUrl()
    clearTimeout(this.toastTimer)
  },
  methods: {
    showToast (message, type = 'success') {
      this.toast.message = message
      this.toast.type = type
      this.toast.icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'
      this.toast.show = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toast.show = false }, 3500)
    },
    showError (message) { this.showToast(message, 'error') },
    showSuccess (message) { this.showToast(message, 'success') },

    triggerVideoUpload () { this.$refs.videoInput.click() },
    triggerAudioUpload () { this.$refs.audioInput.click() },
    triggerScriptUpload () { this.$refs.scriptInput.click() },

    handleVideoSelect (event) {
      const file = event.target.files[0]
      if (file) this.processVideoFile(file)
      event.target.value = ''
    },
    handleAudioSelect (event) {
      const file = event.target.files[0]
      if (file) this.processAudioFile(file)
      event.target.value = ''
    },
    handleScriptSelect (event) {
      const file = event.target.files[0]
      if (file) this.processScriptFile(file)
      event.target.value = ''
    },

    handleVideoDrop (event) {
      this.isDraggingVideo = false
      const file = event.dataTransfer.files[0]
      if (file) this.processVideoFile(file)
    },
    handleAudioDrop (event) {
      this.isDraggingAudio = false
      const file = event.dataTransfer.files[0]
      if (file) this.processAudioFile(file)
    },
    handleScriptDrop (event) {
      this.isDraggingScript = false
      const file = event.dataTransfer.files[0]
      if (file) this.processScriptFile(file)
    },

    processVideoFile (file) {
      if (!file.type.startsWith('video/')) {
        this.showError('请上传有效的视频文件')
        return
      }
      if (file.size > MAX_VIDEO_BYTES) {
        this.showError('视频文件不能超过 2GB')
        return
      }
      this.resetVideoObjectUrl()
      this.selectedVideo = file
      this.videoUrl = URL.createObjectURL(file)
      this.videoDuration = 0
    },

    processAudioFile (file) {
      if (!file.type.startsWith('audio/')) {
        this.showError('请上传有效的音频文件')
        return
      }
      if (file.size > MAX_AUDIO_BYTES) {
        this.showError('音频文件不能超过 50MB')
        return
      }
      this.resetAudioObjectUrl()
      this.selectedAudio = file
      this.audioUrl = URL.createObjectURL(file)
      this.audioDuration = 0
      this.isAudioPlaying = false
    },

    processScriptFile (file) {
      if (!file.name.toLowerCase().endsWith('.txt') && file.type !== 'text/plain') {
        this.showError('视频文案请上传 txt 文件')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const content = String(reader.result || '')
        if (!content.trim()) {
          this.showError('文案文件内容为空')
          return
        }
        this.scriptFile = file
        this.scriptPreview = content.replace(/\s+/g, ' ').trim().slice(0, 120) + (content.trim().length > 120 ? '…' : '')
      }
      reader.onerror = () => this.showError('文案文件读取失败')
      reader.readAsText(file, 'utf-8')
    },

    onVideoLoaded () {
      const video = this.$refs.videoPlayer
      if (video) this.videoDuration = video.duration
    },
    onAudioLoaded () {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      this.audioDuration = audio.duration
      if (!this.isAudioDurationValid) {
        this.showError(`参考音频时长需大于 ${MIN_AUDIO_SECONDS} 秒且小于 ${MAX_AUDIO_SECONDS} 秒，当前为 ${this.formatDuration(this.audioDuration)}`)
      }
    },
    onAudioEnded () {
      this.isAudioPlaying = false
    },

    // 播放 / 暂停参考音频
    toggleAudioPlayback () {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      if (audio.paused) {
        const playPromise = audio.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.showError('参考音频播放失败'))
        }
      } else {
        audio.pause()
      }
    },

    resetVideoObjectUrl () {
      if (this.videoUrl) URL.revokeObjectURL(this.videoUrl)
      this.videoUrl = ''
    },
    resetAudioObjectUrl () {
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl)
      this.audioUrl = ''
    },
    resetVideo () {
      this.resetVideoObjectUrl()
      this.selectedVideo = null
      this.videoDuration = 0
    },
    resetAudio () {
      const audio = this.$refs.audioPlayer
      if (audio) {
        try { audio.pause() } catch (e) { /* ignore */ }
      }
      this.isAudioPlaying = false
      this.resetAudioObjectUrl()
      this.selectedAudio = null
      this.audioDuration = 0
    },
    resetScript () {
      this.scriptFile = null
      this.scriptPreview = ''
      if (this.$refs.scriptInput) this.$refs.scriptInput.value = ''
    },

    formatDuration (seconds) {
      if (!seconds || !isFinite(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${String(s).padStart(2, '0')}`
    },

    generateQiniuFileName (prefix, originalFilename) {
      const now = new Date()
      const pad = (n, len = 2) => String(n).padStart(len, '0')
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}_${pad(now.getMilliseconds(), 3)}`
      const ext = originalFilename.split('.').pop() || 'bin'
      const rand = Math.random().toString(16).slice(2, 6)
      return `${prefix}_${stamp}_${rand}.${ext}`
    },

    startDub () {
      if (!this.isLoggedIn) {
        this.showError('请先登录')
        return
      }
      if (!this.selectedVideo) return this.showError('请先上传视频')
      if (!this.selectedAudio) return this.showError('请先上传参考音频')
      if (!this.scriptFile) return this.showError('请先上传视频文案')
      if (!this.isAudioDurationValid) return this.showError(`参考音频时长需大于 ${MIN_AUDIO_SECONDS} 秒且小于 ${MAX_AUDIO_SECONDS} 秒`)
      if (this.balance < this.estimatedCost) {
        return this.showError(`余额不足，当前余额 ¥${this.balance}，预计需要 ¥${this.estimatedCost.toFixed(2)}`)
      }
      this.confirmCost = this.estimatedCost
      this.showConfirmPricing = true
    },

    async confirmAndStartDub () {
      this.showConfirmPricing = false
      this.isProcessing = true
      const video = this.selectedVideo
      const audio = this.selectedAudio
      const script = this.scriptFile

      this.beginUploadProgress(
        [video, audio, script].reduce((sum, f) => sum + ((f && f.size) || 0), 0)
      )
      try {
        const videoKey = this.generateQiniuFileName('videoDub', video.name)
        const audioKey = this.generateQiniuFileName('videoDub_ref', audio.name)
        const scriptKey = this.generateQiniuFileName('videoDub_script', script.name)

        // 每个文件使用各自的上传凭证（后端会把凭证限定到对应 key，并使用更长的有效期）
        const [videoTokenRes, audioTokenRes, scriptTokenRes] = await Promise.all([
          getUpQiniuToken(videoKey),
          getUpQiniuToken(audioKey),
          getUpQiniuToken(scriptKey)
        ])
        if ([videoTokenRes, audioTokenRes, scriptTokenRes].some(res => !res || res.status === 401)) {
          this.showError('身份验证失败，请重新登录')
          return
        }

        const [videoResp, audioResp, scriptResp] = await Promise.all([
          uploadToQiniu(video, videoKey, videoTokenRes.uploadToken, this.createUploadHandlers(0)),
          uploadToQiniu(audio, audioKey, audioTokenRes.uploadToken, this.createUploadHandlers(1)),
          uploadToQiniu(script, scriptKey, scriptTokenRes.uploadToken, this.createUploadHandlers(2))
        ])
        this.isUploading = false

        const payload = {
          operation_type: 'videoDub',
          input_file_url: `${QINIU_BASE_URL}/${videoResp.data.key}`,
          input_filename: video.name,
          reference_audio_url: `${QINIU_BASE_URL}/${audioResp.data.key}`,
          reference_audio_filename: audio.name,
          script_file_url: `${QINIU_BASE_URL}/${scriptResp.data.key}`,
          script_filename: script.name,
          video_duration: this.videoDuration,
          lipSync: this.lipSync,
          writeSubtitle: this.writeSubtitle
        }

        const response = await saveVideoDubFileUrl(payload)
        if (response && response.status === 200) {
          if (typeof response.balance !== 'undefined') {
            this.$store.commit('setBalance', response.balance)
          }
          this.showSuccess(`任务已提交，预计扣除 ¥${this.confirmCost.toFixed(2)}`)
          this.resetAll()
        } else {
          this.showError((response && response.message) || '服务端返回异常，请稍后重试')
        }
      } catch (error) {
        if (this.isUploadCanceledError(error)) {
          this.showToast('已取消上传', 'info')
        } else if (error && error.message) {
          this.showError(error.message)
        } else {
          this.showError('网络错误或服务异常，请稍后重试')
        }
      } finally {
        this.endUploadProgress()
        this.isProcessing = false
      }
    },

    resetAll () {
      this.resetVideo()
      this.resetAudio()
      this.resetScript()
      this.lipSync = false
      this.writeSubtitle = false
    }
  }
}
</script>

<style scoped>
/* ========== 全局容器 ========== */
.video-translation-view {
  width: 780px;
  min-height: auto;
  background-color: #1a1a1a;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ========== Toast ========== */
.toast-container {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 300px;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.toast-container.success { background-color: #4caf50; color: white; }
.toast-container.error   { background-color: #f44336; color: white; }
.toast-container.info    { background-color: #2196f3; color: white; }

.toast-content { display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 500; }
.toast-icon    { font-size: 20px; font-weight: bold; }
.toast-message { flex: 1; }

/* ========== 主容器 ========== */
.video-translation {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  max-width: 1200px;
  width: 780px;
  min-width: 780px;
  margin: 0 auto;
  padding: 10px;
  color: white;
  background-color: #1a1a1a;
  height: auto;
}

h2 { text-align: center; margin: 10px 0 5px; font-size: 20px; font-weight: 600; }

/* ========== 新增提示小字样式 ========== */
.notice-text {
  font-size: 12px;
  color: #ffb74d;
  text-align: center;
  margin: 0 0 10px 0;
  line-height: 1.5;
  padding: 0 15px;
}

.subtitle-desc { margin-bottom: 15px; color: #bbb; font-size: 15px; text-align: center; }

/* ========== 重新配音 ========== */
.content-area {
  display: flex; gap: 20px; align-items: flex-start;
  justify-content: center; margin-top: 12px;
}

.left-area, .right-container { width: 350px; height: 350px; }

.video-container {
  width: 100%; height: 100%; background-color: #2a2a2a;
  border-radius: 12px; position: relative;
}

.right-container {
  display: flex; flex-direction: column; gap: 12px;
  overflow-y: auto; padding-right: 4px;
}

.upload-box {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  border: 2px dashed #555; border-radius: 12px;
  cursor: pointer; transition: all 0.3s ease;
}

.upload-box:hover, .upload-box.dragging {
  background-color: rgba(0, 188, 212, 0.1); border-color: #00bcd4;
}

.upload-icon { font-size: 48px; color: #00bcd4; margin-bottom: 12px; }
.upload-box p     { font-size: 14px; margin: 4px 0; }
.upload-box small { font-size: 12px; color: #999; }

.video-player-wrapper {
  width: 100%; height: 100%; position: relative;
  border: 2px solid #00bcd4; border-radius: 12px; box-sizing: border-box;
}

.delete-video-btn {
  position: absolute; top: 3px; right: 3px;
  width: 20px; height: 20px; border-radius: 50%;
  background-color: rgba(244, 67, 54, 0.9); border: none;
  color: white; font-size: 20px; cursor: pointer; z-index: 20;
  transition: all 0.2s; display: flex; align-items: center; justify-content: center;
}

.delete-video-btn:hover { background-color: #f44336; transform: scale(1.1); }

.video-file-chip {
  position: absolute; top: 6px; left: 6px; z-index: 20;
  max-width: calc(100% - 40px);
  padding: 3px 8px; border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.6); color: #e0e0e0;
  font-size: 11px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; pointer-events: none;
}

video {
  width: 100%; height: 100%;
  object-fit: contain; background-color: #1a1a1a;
}

/* ─── 设置卡片 ─── */
.setting-card {
  background-color: #2a2a2a; border-radius: 8px; padding: 8px;
  display: flex; align-items: center; gap: 8px;
  transition: background-color 0.2s; cursor: pointer;
}

.setting-card:hover { background-color: #333; }
.setting-card.dragging { background-color: rgba(0, 188, 212, 0.12); }

.setting-content { flex: 1; min-width: 0; }
.setting-content h4 { font-size: 12px; font-weight: 400; margin: 0 0 4px 0; }
.setting-content p  { font-size: 10px; color: #999; margin: 0; line-height: 1.1; }

.setting-content p.file-name {
  font-size: 11px; color: #e0e0e0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.setting-content p.audio-duration-error {
  margin-top: 4px; font-size: 10px; color: #f44336; line-height: 1.3;
}

.setting-control {
  flex-shrink: 0; display: flex; align-items: center;
  gap: 6px; min-height: 34px;
}

.control-hint { font-size: 11px; color: #777; white-space: nowrap; }

.audio-play-btn,
.audio-remove-btn {
  width: 30px; height: 30px; padding: 0;
  border: none; border-radius: 8px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background-color 0.2s, transform 0.2s;
}

.audio-play-btn { background-color: #00bcd4; color: #fff; }
.audio-play-btn:hover { background-color: #0097a7; transform: scale(1.05); }
.audio-play-btn svg { width: 15px; height: 15px; }

.audio-remove-btn { background-color: rgba(244, 67, 54, 0.18); color: #f44336; font-size: 18px; line-height: 1; }
.audio-remove-btn:hover { background-color: rgba(244, 67, 54, 0.32); }

.audio-hidden {
  position: absolute; width: 1px; height: 1px;
  opacity: 0; pointer-events: none;
}

.select-wrapper { position: relative; min-width: 130px; }

.select-wrapper select {
  width: 100%; padding: 8px 32px 8px 12px;
  background-color: #333; color: white;
  border: 1px solid #555; border-radius: 6px;
  appearance: none; cursor: pointer; font-size: 13px;
}

.select-wrapper select:hover { border-color: #00bcd4; }

.toggle-switch {
  position: relative; display: inline-block; width: 38px; height: 15px;
}

.toggle-switch input { opacity: 0; width: 0; height: 0; }

.toggle-slider {
  position: absolute; cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #555; transition: .3s; border-radius: 14px;
}

.toggle-slider:before {
  position: absolute; content: "";
  height: 12px; width: 12px; left: 1.5px; bottom: 1.5px;
  background-color: white; transition: .3s; border-radius: 50%;
}

input:checked + .toggle-slider { background-color: #00bcd4; }

input:checked + .toggle-slider:before { transform: translateX(24px); }

.action-buttons { margin-top: auto; }

.translate-button {
  width: 100%; padding: 8px 0;
  background: linear-gradient(135deg, #00bcd4, #0097a7);
  color: white; border: none; border-radius: 8px;
  cursor: pointer; font-size: 12px; font-weight: 500;
  transition: all 0.2s; box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
}

.translate-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #0097a7, #00838f);
  transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0, 188, 212, 0.4);
}

.translate-button:disabled { background: #555; cursor: not-allowed; box-shadow: none; transform: none; }

.terms { font-size: 12px; color: #888; margin-top: 15px; text-align: center; line-height: 0.5; }

/* ========== 对话框 ========== */
.confirm-dialog-overlay,
.processing-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}

.confirm-dialog, .processing-dialog {
  background-color: #2a2a2a; border-radius: 16px;
  width: 300px; color: white; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
}

.confirm-dialog { padding: 8px; }
.confirm-dialog h3 { font-size: 15px; margin: 0 0 10px; font-weight: 550; }
.confirm-dialog p  { margin: 0 0 12px; color: #ccc; line-height: 1.1; }
.confirm-dialog-buttons { display: flex; justify-content: flex-end; gap: 12px; }

.cancel-btn {
  background-color: transparent; border: 1px solid #666; color: #ccc;
  padding: 8px 16px; border-radius: 8px; cursor: pointer;
  font-size: 10px; font-weight: 500; transition: all 0.2s;
}

.cancel-btn:hover { background-color: rgba(255,255,255,0.1); border-color: #999; color: white; }

.confirm-btn {
  background-color: #f44336; color: white; border: none;
  padding: 8px 16px; border-radius: 8px; cursor: pointer;
  font-size: 10px; font-weight: 500; transition: all 0.2s;
}

.confirm-btn:hover { background-color: #d32f2f; transform: translateY(-2px); }

.processing-dialog { padding: 40px; text-align: center; }

.spinner {
  width: 60px; height: 60px;
  border: 4px solid #444; border-top-color: #00bcd4;
  border-radius: 50%; animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin { to { transform: rotate(360deg); } }

.processing-dialog h3 { margin: 0 0 12px; font-size: 18px; font-weight: 600; }
.processing-dialog p  { margin: 0; color: #999; font-size: 14px; }

/* 上传进度条 + 取消按钮 */
.upload-progress { margin-top: 4px; }

.upload-progress-track {
  width: 100%; height: 8px; border-radius: 4px;
  background-color: #444; overflow: hidden;
}

.upload-progress-bar {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #00bcd4, #0097a7);
  transition: width 0.2s ease-out;
}

.upload-progress-text { margin: 8px 0 0; color: #ccc; font-size: 12px; }
.upload-slow-tip      { margin: 6px 0 0; color: #ffb74d; font-size: 11px; line-height: 1.4; }

.cancel-upload-btn {
  margin-top: 14px; padding: 6px 16px;
  background-color: transparent; border: 1px solid #666; color: #ccc;
  border-radius: 8px; cursor: pointer; font-size: 12px; transition: all 0.2s;
}

.cancel-upload-btn:hover { background-color: rgba(255, 255, 255, 0.1); border-color: #999; color: white; }

/* ========== 移动端 ========== */
@media screen and (max-width: 768px) {
  .toast-container { min-width: 250px; padding: 12px 16px; top: 60px; }
  .toast-content   { font-size: 12px; }
  .toast-icon      { font-size: 16px; }

  .video-translation::before {
    content: "👉 可左右滑动查看完整内容";
    display: block; text-align: center; color: #00bcd4;
    font-size: 12px; padding: 8px;
    background-color: rgba(0, 188, 212, 0.1);
    border-radius: 4px; margin-bottom: 10px;
  }
}
</style>
